/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { useBooking } from "./useBooking";
import { useConfig } from "../lib/react-query/useConfig";
import { computeItemsBreakdown } from "../utils/computeItemsBreakdown";
import { getServiceTotalPrice } from "../utils/service";

const toProductItem = (product) => ({
  price: product.price,
  qty: product.qty ?? 1,
  tax: product.tax,
  currency: product.currency,
});

const withGroupBookingQty = (addons, groupBookingCount) =>
  groupBookingCount > 0 ? addons.map((addon) => ({ ...addon, qty: groupBookingCount })) : addons;

export const useBookingSummary = () => {
  const { selectedServices } = useBooking();
  const { data: config } = useConfig();
  const priceIncludesTax = !!config?.price_includes_tax;

  return useMemo(() => {
    const serviceItems = selectedServices.map((service) => ({
      price: getServiceTotalPrice(service, true),
      qty: 1,
      tax: service.tax,
      currency: service.currency,
    }));

    const productItems = selectedServices.flatMap((service) =>
      [
        ...withGroupBookingQty(service.addons ?? [], service.group_booking_count),
        ...(service.products ?? []),
      ].map(toProductItem)
    );

    const full = computeItemsBreakdown([...serviceItems, ...productItems], { priceIncludesTax });

    const hasDepositEligibleService = selectedServices.some((service) => !!service.deposit_price);

    const depositServiceItems = selectedServices.map((service) => ({
      price: getServiceTotalPrice(service, false),
      qty: 1,
      tax: service.tax,
      currency: service.currency,
    }));

    const depositProductItems = selectedServices.flatMap((service) => {
      const products = [
        ...withGroupBookingQty(service.addons ?? [], service.group_booking_count),
        ...(service.products ?? []),
      ];
      return service.deposit_price
        ? products.filter((p) => p.add_to_deposit).map(toProductItem)
        : products.map(toProductItem);
    });

    const depositBreakdown = hasDepositEligibleService
      ? computeItemsBreakdown([...depositServiceItems, ...depositProductItems], { priceIncludesTax })
      : null;

    return {
      ...full,
      deposit: depositBreakdown?.total ?? null,
    };
  }, [selectedServices, priceIncludesTax]);
};
